import { useMemo } from 'react';

import { Avatar } from '#/components/Avatar/Avatar';
import { AvatarGroup } from '#/components/Avatar/AvatarGroup';
import { Box } from '#/components/Box/Box';
import { Divider } from '#/components/Divider/Divider';
import { Hide } from '#/components/Hide/Hide';
import { LoadingDots } from '#/components/LoadingDots/LoadingDots';
import { Text } from '#/components/Text/Text';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { ApiRsvp } from '#/modules/listing/events/api/types/ApiRsvpResponse';
import { colors, fontSizes } from '#/theme/theme';

type Props = {
  loading?: boolean;
  rsvps?: ApiRsvp[] | null;
  count: number | undefined;
  onClick: () => void;
};

export function ListingViewSidebarEventAttendees({
  loading,
  rsvps,
  count,
  onClick,
}: Props) {
  const { i18n } = useI18n();

  const goingText = useMemo(() => {
    const goingRSVPs =
      rsvps?.filter(
        (rsvp) => rsvp.answer === 'GOING' || rsvp.answer === 'INTERESTED',
      ) ?? [];

    let text = '';
    if (goingRSVPs.length === 1) {
      text = i18n('%(name)s', {
        name: goingRSVPs[0].profile.name,
      });
    } else if (goingRSVPs.length === 2) {
      text = i18n('%(nameOne)s and %(nameTwo)s', {
        nameOne: goingRSVPs[0].profile.name,
        nameTwo: goingRSVPs[1].profile.name,
      });
    } else if (goingRSVPs.length === 3) {
      text = i18n('%(nameOne)s, %(nameTwo)s, and 1 other', {
        nameOne: goingRSVPs[0].profile.name,
        nameTwo: goingRSVPs[1].profile.name,
      });
    } else if (goingRSVPs.length > 3) {
      text = i18n('%(nameOne)s, %(nameTwo)s, and %(remaining)s others', {
        nameOne: goingRSVPs[0].profile.name,
        nameTwo: goingRSVPs[1].profile.name,
        remaining: (count ?? 0) - 2,
      });
    }

    return text;
  }, [i18n, rsvps, count]);

  const latestGoingRsvps = useMemo(() => {
    const goingRSVPs = rsvps?.filter(
      (rsvp) => rsvp.answer === 'GOING' || rsvp.answer === 'INTERESTED',
    );

    return goingRSVPs && goingRSVPs.length > 5
      ? goingRSVPs.slice(0, 5)
      : goingRSVPs;
  }, [rsvps]);

  return (
    <Hide xs sm md>
      <Box display="flex" gridGap={3} flexDirection="column">
        <Text color={colors.lightContentGrey}>
          {i18n('Going or Interested')}
        </Text>

        {loading && !rsvps && (
          <div style={{ position: 'relative', left: '12px' }}>
            <LoadingDots size="text" />
          </div>
        )}

        {rsvps?.length === 0 && i18n('No one going yet')}

        {latestGoingRsvps && latestGoingRsvps.length > 0 && (
          <>
            <AvatarGroup
              onClick={onClick}
              data-qa-id="event-attendees-avatars"
              $stacked
              style={{ cursor: 'pointer' }}
            >
              {latestGoingRsvps.map((rsvp) => (
                <Avatar
                  key={rsvp.profile.userId}
                  image={rsvp.profile.image}
                  name={rsvp.profile.name}
                  size="xs"
                />
              ))}
            </AvatarGroup>
            <Text fontSize={fontSizes[14]} color={colors.brandBlue}>
              {goingText}
            </Text>
          </>
        )}
      </Box>
      <Divider marginTop={24} marginBottom={24} />
    </Hide>
  );
}
